import { computed, observable } from "@legendapp/state";
import { auth, googleAuthProvider } from "../../appConfigs/firebase";
import { getRedirectResult, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { LoginWithGoogleReq } from "_types/requests/auth";
import { CurrentUser } from "_types/responses/auth";
import { JWTData } from "_types";
import { decodeToken } from 'react-jwt';

export const AuthService = new class {
    private $token = observable<string>('');
    private $requiresTwoFactor = observable(false);
    private _$shouldSelectCourt = observable(false);
    private _$isLoggedAs = observable(false);
    private _registering2FA = false;

    public currentUser?: CurrentUser;
    public $currentUser = observable<CurrentUser | undefined>(undefined);
    public $courtSelected = observable<string | undefined>(undefined);

    public $isTwoFactorRequired = computed(() => this.$requiresTwoFactor.get());
    public $shouldSelectCourt = computed(() => this._$shouldSelectCourt.$);
    public $isLoggedIn = computed(() => !!this.$token.get());
    public $isLoggedAs = computed(() => this._$isLoggedAs.get());
    public $currentCourt = computed(() => this.$currentUser.$?.courts?.find(c => c.id === this.$courtSelected.$));

    public get isAdmin() {
        return !this.currentUser?.courts.length;
    }

    public get token() {
        return this.$token.get();
    }

    public get requiresTwoFactor(): boolean {
        return this.$requiresTwoFactor.get();
    }

    public twoFactorRequired() {
        this.$requiresTwoFactor.set(true);
    }

    public twoFactorResolved() {
        this.$requiresTwoFactor.set(false);
    }

    public isLoggedIn() {
        return !!this.$token.get();
    }

    public get registering2FA() {
        return this._registering2FA;
    }

    public registering2FAStart() {
        this._registering2FA = true;
    }

    public registering2FAEnd() {
        this._registering2FA = false;
    }

    public loadTokens() {
        const token = localStorage.getItem("jwt") ?? '';
        this.$requiresTwoFactor.set(!!localStorage.getItem("requiresTwoFactor"));
        this.$token.set(token);
        this.checkIfShouldSelectCourt(token);

        if (localStorage.getItem('loggedAs')) {
            this._$isLoggedAs.set(true);
        }
    }

    public setToken(token: string, requireTwoFactor = false) {
        this.$requiresTwoFactor.set(requireTwoFactor);
        this.checkIfShouldSelectCourt(token);

        localStorage.setItem('jwt', token);
        localStorage.setItem('requiresTwoFactor', requireTwoFactor ? '1' : '');
        this.$token.set(token);
    }

    public clearAll() {
        this.$requiresTwoFactor.set(false);
        this.$token.set('');
        this._$shouldSelectCourt.set(false);
        this._$isLoggedAs.set(false);
        localStorage.removeItem('jwt');
        localStorage.removeItem('requiresTwoFactor');
        localStorage.removeItem('shouldSelectCourt');
        localStorage.removeItem('loggedAs');
    }

    public clearLoggedAs() {
        this._$isLoggedAs.set(false);
        localStorage.removeItem('admin_jwt');
        localStorage.removeItem('loggedAs');
    }

    public loggedAs(token: string) {
        localStorage.setItem('old_admin_path', window.location.pathname + window.location.search);
        localStorage.setItem('admin_jwt', this.$token.get());
        localStorage.setItem('loggedAs', '1');
        // this._$isLoggedAs.set(true);
        this.setToken(token);

        setTimeout(() => {
            window.location.pathname = '/admin';
        }, 1000);
    }

    public backToAdmin() {
        this.setToken(localStorage.getItem('admin_jwt')!);
        localStorage.removeItem('admin_jwt');
        localStorage.removeItem('loggedAs');

        const oldPath = localStorage.getItem('old_admin_path');
        window.location.href = oldPath ? `${window.origin}${oldPath}` : '/admin';
    }

    public async retrieveGoogleToken(captchaToken: string): Promise<LoginWithGoogleReq | undefined> {
        googleAuthProvider.setCustomParameters({
            prompt: 'select_account',
        });
        googleAuthProvider.addScope('email');

        const result = await getRedirectResult(auth);

        if (result) {

            const credential = GoogleAuthProvider.credentialFromResult(result);
            if (credential == null) {
                return;
            }

            return {
                captchaToken,
                email: result.user.email!,
                idToken: credential.idToken!,
                accessToken: credential.accessToken!,
            };
        }

        const popout = await signInWithPopup(auth, googleAuthProvider);
        const credential = GoogleAuthProvider.credentialFromResult(popout);

        if (credential == null) {
            return;
        }

        return {
            captchaToken,
            email: popout.user.email!,
            idToken: credential.idToken!,
            accessToken: credential.accessToken!,
        };
    }

    public logout() {
        this.clearAll();
    }

    private checkIfShouldSelectCourt(token: string) {
        const r = decodeToken<JWTData>(token);

        if (r?.SessionType && r?.SessionType == "CourtSelection") {
            this._$shouldSelectCourt.set(true);
            this.$requiresTwoFactor.set(false);
            localStorage.setItem('shouldSelectCourt', '1');
            localStorage.removeItem('requiresTwoFactor');
        }
        else {
            this._$shouldSelectCourt.set(false);
            localStorage.removeItem('shouldSelectCourt');
        }

        if (r?.CourtId) {
            this.$courtSelected.set(r.CourtId);
        } else if (this.$courtSelected.$) {
            this.$courtSelected.set(undefined);
        }
    }
}
